/**
 * ContourEditorPanel — AK-321 «Редактор контура — обвести поле» v1.1 + AK-371 v2.
 *
 * Плавающая панель редактора: инструменты рисования фигур, live-площадь (га
 * крупно), булевы «Вырезать»/«Объединить», действия «Очистить»/«Готово».
 * Монтируется в v2.tsx под styleReady когда editorActive=true. Только
 * @/components/ui/* + Lucide + Earth Tech токены.
 *
 * AK-371 v2 модель «фигуры-первыми» (founder-UAT #443):
 *   - Три инструмента: Круг, Прямоугольник, Полигон — рисование ДОБАВЛЯЕТ фигуру
 *     к набору (ничего не стирается).
 *   - Круг: два режима — «от центра» / «от края» (диаметр).
 *   - Фигуры пересекаются → включаются «Вырезать» / «Объединить».
 *   - «Вырезать» → режим выбора цели кликом (подсказка «кликните фигуру»).
 *
 * Панель НЕ держит геометрию — читает состояние из editorStore (его пишет
 * ContourEditorController) и диспатчит сигналы. Позиционируется top-left.
 */
import {
  PencilRuler,
  Eraser,
  X,
  Loader2,
  Undo2,
  Redo2,
  Square,
  Circle,
  Spline,
  Scissors,
  Combine,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEditorStore, type ShapeTool } from '@/lib/map/editor/editorStore';

interface ToolDef {
  key: ShapeTool;
  label: string;
  hint: string;
  icon: typeof Square;
}
/** Три примитива-инструмента. Рисование любого ДОБАВЛЯЕТ фигуру к набору. */
const TOOLS: ToolDef[] = [
  { key: 'circle', label: 'Круг', hint: 'Тащи от центра к краю', icon: Circle },
  { key: 'rectangle', label: 'Прямоугольник', hint: 'Тащи угол-в-угол', icon: Square },
  { key: 'polygon', label: 'Полигон', hint: 'Клики по точкам, замкни', icon: Spline },
];

export function ContourEditorPanel() {
  const loading = useEditorStore((s) => s.loading);
  const error = useEditorStore((s) => s.error);
  const hasFeature = useEditorStore((s) => s.hasFeature);
  const result = useEditorStore((s) => s.result);
  const snapping = useEditorStore((s) => s.snapping);
  const canUndo = useEditorStore((s) => s.canUndo);
  const canRedo = useEditorStore((s) => s.canRedo);
  const shapeCount = useEditorStore((s) => s.shapeCount);
  const canCut = useEditorStore((s) => s.canCut);
  const cutMode = useEditorStore((s) => s.cutMode);
  const activeTool = useEditorStore((s) => s.activeTool);
  const finishDrawing = useEditorStore((s) => s.finishDrawing);
  const circleMode = useEditorStore((s) => s.circleMode);
  const deactivate = useEditorStore((s) => s.deactivate);
  const clearFeature = useEditorStore((s) => s.clearFeature);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const drawShape = useEditorStore((s) => s.drawShape);
  const unionShapes = useEditorStore((s) => s.unionShapes);
  const toggleCut = useEditorStore((s) => s.toggleCut);
  const setCircleMode = useEditorStore((s) => s.setCircleMode);

  return (
    <div
      data-testid="editor-panel"
      style={{ position: 'absolute', top: 16, left: 16, zIndex: 32, width: 300 }}
      // Непрозрачная поверхность (bg-popover), НЕ полупрозрачность над картой —
      // иначе спутник просвечивает и кнопки редактора теряются (AK-382/AK-384).
      className="rounded-lg border border-border bg-popover text-popover-foreground shadow-lg p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <PencilRuler size={16} className="text-accent-brand" />
          <span>Обвести поле</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Отменить (Ctrl+Z)"
            title="Отменить (Ctrl+Z)"
            disabled={!canUndo}
            data-testid="editor-undo"
            onClick={() => undo()}
          >
            <Undo2 size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Вернуть (Ctrl+Shift+Z)"
            title="Вернуть (Ctrl+Shift+Z)"
            disabled={!canRedo}
            data-testid="editor-redo"
            onClick={() => redo()}
          >
            <Redo2 size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="Закрыть редактор"
            data-testid="editor-close"
            onClick={() => deactivate()}
          >
            <X size={16} />
          </Button>
        </div>
      </div>

      {/* AK-371 v2 палитра инструментов: круг / прямоугольник / полигон.
          Рисование ДОБАВЛЯЕТ фигуру к набору. */}
      <div data-testid="editor-tools" className="mb-2">
        <div className="grid grid-cols-3 gap-2">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const selected = activeTool === t.key;
            return (
              <Button
                key={t.key}
                variant={selected ? 'default' : 'outline'}
                size="sm"
                className="flex-col h-auto py-1 gap-1"
                data-testid={`editor-tool-${t.key}`}
                title={t.hint}
                onClick={() => drawShape(t.key)}
              >
                <Icon size={16} />
                <span className="text-xs leading-none">{t.label}</span>
              </Button>
            );
          })}
        </div>

        {/* Круг: переключатель режима «от центра» / «от края» (диаметр). */}
        {activeTool === 'circle' && (
          <div className="mt-2 flex items-center gap-2" data-testid="editor-circle-mode">
            <Button
              variant={circleMode === 'center' ? 'default' : 'outline'}
              size="sm"
              className="flex-1 h-7 text-xs"
              data-testid="editor-circle-center"
              onClick={() => setCircleMode('center')}
            >
              От центра
            </Button>
            <Button
              variant={circleMode === 'diameter' ? 'default' : 'outline'}
              size="sm"
              className="flex-1 h-7 text-xs"
              data-testid="editor-circle-diameter"
              onClick={() => setCircleMode('diameter')}
            >
              От края
            </Button>
          </div>
        )}

        {/* Булевы: «Вырезать» (нужно пересечение) / «Объединить» (≥2 фигур). */}
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Button
            variant={cutMode ? 'default' : 'outline'}
            size="sm"
            className="h-8"
            disabled={!canCut && !cutMode}
            data-testid="editor-cut"
            title="Вырезать одну фигуру из другой (кликните по фигуре)"
            onClick={() => toggleCut()}
          >
            <Scissors size={14} />
            <span className="text-xs">Вырезать</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            disabled={shapeCount < 2}
            data-testid="editor-union"
            title="Объединить все фигуры в одно поле"
            onClick={() => unionShapes()}
          >
            <Combine size={14} />
            <span className="text-xs">Объединить</span>
          </Button>
        </div>
      </div>

      {/* Live-результат: площадь (га) крупно, периметр мелко, либо подсказки. */}
      <div
        data-testid="editor-result"
        className="rounded-md border border-border bg-muted/40 px-3 py-2 mb-2 min-h-[58px] flex flex-col justify-center"
      >
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 size={14} className="animate-spin" />
            Загрузка геометрии поля…
          </div>
        ) : cutMode ? (
          <div className="text-xs text-accent-brand font-medium" data-testid="editor-cut-hint">
            Кликните фигуру, которую нужно вырезать из остальных.
          </div>
        ) : error ? (
          <div className="text-xs text-destructive">{error}</div>
        ) : result ? (
          <>
            <div className="text-xl font-bold tabular-nums text-foreground leading-tight">
              {result.primary}
            </div>
            {result.secondary && (
              <div className="text-xs text-muted-foreground tabular-nums mt-1">
                {result.secondary}
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              {result.vertexCount} вершин · перетащите вершину · точка на ребре —
              добавить · двойной клик — удалить
            </div>
          </>
        ) : (
          <div className="text-xs text-muted-foreground" data-testid="editor-hint">
            {/* Подсказка фазы рисования живёт здесь, а не в «+ Поле»: та
                панель на время редактора размонтирована (v2.tsx), и человек,
                впервые рисующий поле, не видел ни слова о том, что делать. */}
            {activeTool === 'polygon'
              ? 'Кликайте по углам поля. Замкните на первой точке — или нажмите «Готово», и контур замкнётся сам (нужно минимум 3 точки).'
              : 'Выберите инструмент и нарисуйте фигуру — или кликните по полю на карте. Фигуры складываются в одно поле.'}
          </div>
        )}
      </div>

      {/* Индикатор прилипания к границе соседнего поля. */}
      {snapping && !cutMode && (
        <div
          data-testid="editor-snapping"
          className="mb-2 text-xs font-medium text-accent-brand flex items-center gap-1"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-accent-brand" />
          Прилипание к границе поля
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          disabled={!hasFeature}
          data-testid="editor-clear"
          onClick={clearFeature}
        >
          <Eraser size={14} />
          Очистить
        </Button>
        <Button
          variant="default"
          size="sm"
          className="flex-1"
          data-testid="editor-done"
          onClick={() => {
            // Контур уже есть — просто выходим. Если человек рисовал и не
            // замкнул, замыкаем за него (стандарт ГИС) и выходим только при
            // успехе: раньше «Готово» на незамкнутом контуре молча стирало
            // всю работу — редактор закрывался, кольца не было, форма
            // получала reset().
            if (hasFeature) {
              deactivate();
              return;
            }
            finishDrawing();
            if (useEditorStore.getState().hasFeature) deactivate();
          }}
        >
          Готово
        </Button>
      </div>
    </div>
  );
}
